import React, { useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { Button, Modal } from "antd";
import { EditOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { Favorite, FavoriteBorder } from "@mui/icons-material";
import { IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button as MUIButton } from "@mui/material";

const ProductCard = ({
  product,
  onStatusChange,
  onClose,
  showActions = true,
  showAuthor = false,
  isAdmin = false,
  statuses = ["active", "pending", "closed"],
  statusChangeLoading = null,
  showStatus = false,
  getLocationName = null,
  isDashboard = false,
  onEdit,
}) => {
  const API = process.env.REACT_APP_API.replace(/\/api$/, "");

  const [showDialog, setShowDialog] = useState(false);

  //format currency (padrão brasileiro)
  Number.prototype.format = function (n, x) {
    return this.toFixed(Math.max(0, ~~n))
      .replace(/\d(?=(\d{3})+(?!\d))/g, "$&.")
      .replace(/\.(\d{2})$/, ",$1");
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "#10b981";
      case "pending":
        return "#f59e0b";
      case "closed":
        return "#6b7280";
      default:
        return "#6b7280";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "active":
        return "Ativo";
      case "pending":
        return "Pendente";
      case "closed":
        return "Encerrado";
      default:
        return status;
    }
  };

  return (
    <Link to={`/product/${product._id}`} style={{minWith:20}}>
      <div
        className={`modern-product-card ${isDashboard ? "dashboard-card" : ""}`}
      >
        {/* Imagem com aspect ratio */}
        <div className="product-image-container">
          <img
            src={
              product.images?.[0]
                ? `${API}${product.images[0]}`
                : "/default-image.jpg"
            }
            alt={product.name}
            className="product-image"
          />
          {/* Botão de fechar */}
          {!isAdmin && product.status !== "closed" && onClose && (
            <div
              className="close-button-container"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <button
                className="btn btn-outline-danger btn-sm close-btn"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowDialog(true);
                }}
              >
                <CloseCircleOutlined className="close-icon" />
              </button>
            </div>
          )}
        </div>
        {/* Conteúdo do produto */}
        <div className="product-content">
          {/* Nome e preço */}
          <div className="product-header">
            <div className="product-title-section">
              <h6 className="card-title mb-1 text-dark fw-bold product-title">
                {product.name}
              </h6>
              <h5 className="text-success fw-bold mb-0 product-price">
                {new Intl.NumberFormat("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                }).format(product.price)}
              </h5>
            </div>
            {/* Ações */}
            {showActions && (
              <div
                className="d-flex gap-1 flex-shrink-0 align-items-center"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                {onStatusChange && (
                  <select
                    className="form-select form-select-sm shadow-none mb-0 status-select"
                    aria-label="Selecionar status"
                    value={product.status}
                    onChange={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onStatusChange(e, product._id);
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    disabled={statusChangeLoading === product._id}
                  >
                    <option value={product.status} disabled>
                      {getStatusText(product.status)}
                    </option>
                    {Array.isArray(
                      typeof statuses !== "undefined" ? statuses : []
                    ) &&
                      (statuses || ["active", "pending", "closed"])
                        .filter((s) => s !== product.status)
                        .map((s, i) => (
                          <option key={i} value={s}>
                            {getStatusText(s)}
                          </option>
                        ))}
                  </select>
                )}
                {onEdit && (
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onEdit(product._id);
                    }}
                    title="Editar produto"
                  >
                    <EditOutlined />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Características de imóvel (se aplicável) OU Descrição */}
          {product.type === "produto" &&
          (product.bedrooms || product.bathrooms || product.garages) ? (
            <div className="property-features">
              {product.bedrooms && (
                <div className="feature-item" title="Dormitórios">
                  <i className="fas fa-bed"></i>
                  <span className="feature-text">{product.bedrooms}</span>
                </div>
              )}
              {product.bathrooms && (
                <div className="feature-item" title="Banheiros">
                  <i className="fas fa-bath"></i>
                  <span className="feature-text">{product.bathrooms}</span>
                </div>
              )}
              {product.garages && (
                <div className="feature-item" title="Garagens">
                  <i className="fas fa-car"></i>
                  <span className="feature-text">{product.garages}</span>
                </div>
              )}
            </div>
          ) : product.description ? (
            <div className="product-description">
              <p className="text-muted text-truncate-2 mb-2">
                {product.description}
              </p>
            </div>
          ) : null}
        </div>
        {/* Footer com localização, autor e data */}
        <div className="product-footer">
          <div className="location-section">
            <small className="text-muted">
              <i className="fas fa-map-marker-alt me-1"></i>
              {getLocationName
                ? getLocationName(product)
                : product.neighborhood?.name || product.city?.name || (typeof product.location === "object"
                ? product.location?.name
                : product.location)}
            </small>
            {showAuthor && product.author && (
              <small className="text-muted">
                <i className="fas fa-user me-1"></i>
                {product.author.name}
              </small>
            )}
          </div>
        </div>
      </div>
      <Dialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
      >
        <DialogTitle>Confirmar Encerramento</DialogTitle>
        <DialogContent>
          <p>
            Tem certeza que deseja encerrar este produto? Esta ação não pode
            ser desfeita.
          </p>
        </DialogContent>
        <DialogActions>
          <MUIButton onClick={(e) => {setShowDialog(false); e.stopPropagation()}}>Cancelar</MUIButton>
          <MUIButton onClick={(e) => { onClose(product._id); setShowDialog(false); e.stopPropagation()}} color="error">
            Sim, Encerrar
          </MUIButton>
        </DialogActions>
      </Dialog>
    </Link>
  );
};

export default ProductCard;
